import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import Box from 'elements/Box';
import { ProjectConfig } from 'Global';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import LoadingButton from '@mui/lab/LoadingButton';
import Cookies from 'universal-cookie';
import { loadLabels, getHomeLabel } from 'hooks/useLabels';
import TopBar from 'elements/TopBar/TopBar';

const cookies = new Cookies();

interface Lang {
  lang_id: string;
  lang: string;
}

// Common timezones list
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Bogota', label: 'Bogotá, Lima, Quito' },
  { value: 'America/Mexico_City', label: 'Ciudad de México' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
  { value: 'America/Sao_Paulo', label: 'São Paulo' },
  { value: 'America/Santiago', label: 'Santiago' },
  { value: 'America/Caracas', label: 'Caracas' },
  { value: 'Europe/Madrid', label: 'Madrid' },
  { value: 'Europe/Paris', label: 'París' },
  { value: 'Europe/Rome', label: 'Roma' },
  { value: 'Europe/London', label: 'Londres' },
  { value: 'Europe/Berlin', label: 'Berlín' },
  { value: 'Asia/Tokyo', label: 'Tokio' },
  { value: 'Asia/Shanghai', label: 'Shanghái' },
  { value: 'Asia/Manila', label: 'Manila' },
  { value: 'Australia/Sydney', label: 'Sídney' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
];

export default function Cuenta(props: any) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [langs, setLangs] = useState<Lang[]>([]);
  const [selectedLang, setSelectedLang] = useState<string>(
    localStorage.getItem('lang_id') || '1'
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    localStorage.getItem('timezone') || 'America/Bogota'
  );

  const user_id = cookies.get('user_id');
  const user_name = cookies.get('user_name');

  // Load languages list and user settings
  useEffect(() => {
    props.setLoading(true);
    setLoading(true);

    // Load languages
    axios
      .get(ProjectConfig.api_url, { params: { request: 'lang_list' } })
      .then((res) => {
        setLangs(res.data.lang_list);
      })
      .catch((err) => console.error('Error loading langs:', err));

    // Load user account settings
    axios
      .get(ProjectConfig.api_url, {
        params: {
          request: 'account_get',
          user_id: user_id,
        },
      })
      .then((res) => {
        if (res.data.status === 'success') {
          const data = res.data.data;
          setSelectedLang(data.lang_id?.toString() || '1');
          setSelectedTimezone(data.timezone || 'America/Bogota');
          
          // Update localStorage with server values
          localStorage.setItem('lang_id', data.lang_id?.toString() || '1');
          localStorage.setItem('timezone', data.timezone || 'America/Bogota');
        }
        setLoading(false);
        props.setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading account:', err);
        setLoading(false);
        props.setLoading(false);
      });
  }, []);

  // Handle save
  const handleSave = () => {
    setSaving(true);

    axios
      .get(ProjectConfig.api_url, {
        params: {
          request: 'account_update',
          user_id: user_id,
          lang_id: selectedLang,
          timezone: selectedTimezone,
        },
      })
      .then((res) => {
        if (res.data.status === 'success') {
          enqueueSnackbar('Configuración guardada', { variant: 'success' });

          // Update localStorage
          localStorage.setItem('lang_id', selectedLang);
          localStorage.setItem('timezone', selectedTimezone);

          // Reload labels with new language and return to home
          loadLabels(selectedLang).then(() => {
            window.location.href = '/';
          });
        } else {
          enqueueSnackbar(res.data.message || 'Error al guardar', {
            variant: 'error',
          });
        }
        setSaving(false);
      })
      .catch((err) => {
        console.error('Error saving account:', err);
        enqueueSnackbar('Error al guardar configuración', { variant: 'error' });
        setSaving(false);
      });
  };

  return (
    <div id="Cuenta" className="module_container">
      <div className="module_topBar">
        <TopBar title={getHomeLabel('cuenta')} />
      </div>

      <div className="module_content">
        <Box>
          <Typography variant="h6" gutterBottom>
            {user_name}
          </Typography>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Preferencias
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="lang-label">Idioma</InputLabel>
            <Select
              labelId="lang-label"
              id="lang"
              value={selectedLang}
              label="Idioma"
              onChange={(e) => setSelectedLang(e.target.value)}
              disabled={loading}
            >
              {langs.map((lang) => (
                <MenuItem key={lang.lang_id} value={lang.lang_id}>
                  {lang.lang}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="timezone-label">Zona Horaria</InputLabel>
            <Select
              labelId="timezone-label"
              id="timezone"
              value={selectedTimezone}
              label="Zona Horaria"
              onChange={(e) => setSelectedTimezone(e.target.value)}
              disabled={loading}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LoadingButton
            size="large"
            onClick={handleSave}
            loading={saving}
            fullWidth
            variant="contained"
            sx={{ mt: 3, fontSize: '14px', padding: '12px 0px' }}
          >
            Guardar Cambios
          </LoadingButton>
        </Box>
      </div>
    </div>
  );
}
